import { toMs } from "@/lib/utils";
import type { HeuristicJob, Job } from "@/schemas/firestore/jobs.schema";
import { useStore } from "@/store";
import { useEffect, useRef, useState } from "react";

/**
 * Hook for jobs (pending + completed with recommendations)
 */
export const useJobs = () => {
  // Note: Subscription to jobs is handled centrally in useStoreInitializer
  const {
    jobs,
    currentJobId,
    isLoadingJobs,
    jobsError,
    setCurrentJob,
    startJob,
    userData,
  } = useStore();

  const getAllJobs = () =>
    Object.values(jobs).sort(
      (a, b) => toMs(b.created_at) - toMs(a.created_at),
    ) as Job[]; // Cast to Job[]

  /**
   * Completed jobs that have any kind of recommendation.
   * When a domain is selected, scope to that domain; otherwise show all domains
   * (matches the pages/ideas tabs behavior).
   */
  const completedJobsWithRecommendations = getAllJobs().filter((job: Job) => {
    const heuristicJob = job as HeuristicJob;
    const hasRec = !!heuristicJob.results?.ai_recommendation?.valid_json;
    const is_matching_domain = userData?.currentDomainId
      ? job.domain_id === userData.currentDomainId
      : true;

    return is_matching_domain && job.status === "completed" && hasRec;
  });

  const getCurrentJob = () =>
    currentJobId ? (jobs[currentJobId] as Job) : null;

  const analyzeUrl = async (url: string) => {
    return startJob(url);
  };

  const [recentErrorJob, setRecentErrorJob] = useState<Job | null>(null);
  const prevJobsRef = useRef<Record<string, Job>>({});

  useEffect(() => {
    const prevJobs = prevJobsRef.current;

    Object.values(jobs).forEach((job) => {
      const prev = prevJobs[job.id];

      const isErrorNow =
        job.status === "error" && (!prev || prev.status !== "error");
      const msSinceUpdate = Date.now() - toMs(job.updated_at ?? job.created_at);
      const isRecent = msSinceUpdate < 30_000;
      const isOnActiveDomain = job.domain_id === userData?.currentDomainId;

      if (isErrorNow && isRecent && isOnActiveDomain) {
        setRecentErrorJob(job);
      }
    });

    prevJobsRef.current = jobs;
  }, [jobs, userData?.currentDomainId]);

  return {
    jobs: getAllJobs(),
    pendingJobs: getAllJobs().filter((j) => j.status === "pending"),
    completedJobs: getAllJobs().filter((j) => j.status === "completed"),
    completedJobsWithRecommendations,
    jobsMap: jobs as Record<string, Job>, // Cast to Record<string, Job>
    currentJob: getCurrentJob(),
    currentJobId,
    isLoadingJobs,
    hasPending: Object.values(jobs).some((j) => j.status === "pending"),
    error: jobsError,
    setCurrentJob,
    analyzeUrl,
    recentErrorJob,
  };
};
